import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  Plus,
  Pencil,
  UserCheck,
  Users,
  Euro,
  Clock,
  CheckCircle2,
  XCircle,
  HandCoins,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const TABS = [
  { key: "affiliates", label: "Affilies", icon: Users },
  { key: "commissions", label: "Commissions", icon: HandCoins },
];

function AffiliateForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || { name: "", email: "", phone: "", commission_rate: 10, notes: "" }
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Nom et email requis");
      return;
    }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="affiliate-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Nom</Label>
          <Input data-testid="affiliate-name-input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Nom de l'affilie" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Email</Label>
          <Input data-testid="affiliate-email-input" value={form.email} type="email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="email@example.com" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Telephone</Label>
          <Input data-testid="affiliate-phone-input" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="06 XX XX XX XX" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Taux de commission (%)</Label>
          <Input data-testid="affiliate-rate-input" type="number" step="0.1" min="0" max="100"
            value={form.commission_rate}
            onChange={(e) => setForm({ ...form, commission_rate: Number(e.target.value) })}
            className="bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-[#94A3B8]">Notes</Label>
        <Input data-testid="affiliate-notes-input" value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Notes internes..." />
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}
          className="border-[#2A3441] text-[#94A3B8]" data-testid="affiliate-cancel-btn">Annuler</Button>
        <Button type="submit" disabled={saving}
          className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="affiliate-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer
        </Button>
      </div>
    </form>
  );
}

function AffiliatesTab() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/affiliates`);
      setAffiliates(res.data);
    } catch { toast.error("Erreur chargement affilies"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = async (data) => {
    await axios.post(`${API}/affiliates`, data);
    toast.success("Affilie cree"); setCreating(false); await fetch();
  };
  const update = async (data) => {
    await axios.put(`${API}/affiliates/${editing.id}`, data);
    toast.success("Affilie mis a jour"); setEditing(null); await fetch();
  };
  const deactivate = async (id) => {
    if (!window.confirm("Desactiver cet affilie ?")) return;
    await axios.delete(`${API}/affiliates/${id}`);
    toast.success("Affilie desactive"); await fetch();
  };

  const active = affiliates.filter(a => a.status === "active");
  const inactive = affiliates.filter(a => a.status === "inactive");

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>;

  return (
    <div className="space-y-6" data-testid="affiliates-tab">
      <div className="flex items-center justify-between">
        <p className="text-[#94A3B8]">{active.length} affilie{active.length > 1 ? "s" : ""} actif{active.length > 1 ? "s" : ""}</p>
        {!creating && !editing && (
          <Button onClick={() => setCreating(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="new-affiliate-btn">
            <Plus className="h-4 w-4 mr-2" />Nouvel affilie
          </Button>
        )}
      </div>

      {creating && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Nouvel affilie</h3>
          <AffiliateForm onSave={create} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Modifier l'affilie</h3>
          <AffiliateForm initial={editing} onSave={update} onCancel={() => setEditing(null)} />
        </div>
      )}

      {active.length === 0 && !creating ? (
        <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <UserCheck className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun affilie</h3>
          <p className="text-[#94A3B8]">Creez votre premier affilie pour suivre les commissions.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map(a => (
            <div key={a.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5 hover:border-[#0099CC]/30 transition-colors"
              data-testid={`affiliate-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-semibold">{a.name}</p>
                  <p className="text-xs text-[#64748b] mt-0.5">{a.email}</p>
                </div>
                <Badge className="bg-[#10B981]/20 text-[#10B981] border-0">{a.commission_rate}%</Badge>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-white">{a.clients_count || 0}</p>
                  <p className="text-[10px] text-[#64748b] uppercase">Clients</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[#10B981]">{(a.total_earned || 0).toLocaleString("fr-FR")}</p>
                  <p className="text-[10px] text-[#64748b] uppercase">Total EUR</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-[#F59E0B]">{(a.pending_amount || 0).toLocaleString("fr-FR")}</p>
                  <p className="text-[10px] text-[#64748b] uppercase">En attente</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 border-[#2A3441] text-[#94A3B8] hover:text-white"
                  onClick={() => { setCreating(false); setEditing(a); }} data-testid={`edit-affiliate-${a.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
                </Button>
                <Button variant="outline" size="sm" className="border-[#2A3441] text-[#EF4444] hover:bg-[#EF4444]/10"
                  onClick={() => deactivate(a.id)} data-testid={`deactivate-affiliate-${a.id}`}>
                  <XCircle className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <div>
          <p className="text-sm text-[#64748b] mb-3">Affilies inactifs ({inactive.length})</p>
          <div className="space-y-2">
            {inactive.map(a => (
              <div key={a.id} className="bg-[#151B2B]/50 border border-[#2A3441]/50 rounded-lg p-3 flex items-center gap-3 opacity-60">
                <p className="text-[#94A3B8] flex-1">{a.name} - {a.email}</p>
                <Badge className="bg-[#64748b]/20 text-[#64748b] border-0">Inactif</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommissionsTab() {
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter !== "all" ? `?status=${filter}` : "";
      const [commsRes, summRes] = await Promise.all([
        axios.get(`${API}/commissions${statusParam}`),
        axios.get(`${API}/commissions/summary`),
      ]);
      setCommissions(commsRes.data);
      setSummary(summRes.data);
    } catch { toast.error("Erreur chargement commissions"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const markPaid = async (id) => {
    try {
      await axios.put(`${API}/commissions/${id}/pay`);
      toast.success("Commission marquee comme payee");
      await fetch();
    } catch { toast.error("Erreur"); }
  };

  const statusCfg = {
    pending: { label: "En attente", icon: Clock, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
    paid: { label: "Payee", icon: CheckCircle2, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
    cancelled: { label: "Annulee", icon: XCircle, color: "text-[#64748b]", bg: "bg-[#64748b]/10" },
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>;

  return (
    <div className="space-y-6" data-testid="commissions-tab">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid="comm-total">
            <p className="text-xs text-[#64748b] uppercase tracking-wider">Total commissions</p>
            <p className="text-xl font-bold text-white mt-1">{summary.total_commissions.toLocaleString("fr-FR")} EUR</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid="comm-pending">
            <p className="text-xs text-[#64748b] uppercase tracking-wider">En attente</p>
            <p className="text-xl font-bold text-[#F59E0B] mt-1">{summary.pending_commissions.toLocaleString("fr-FR")} EUR</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid="comm-paid">
            <p className="text-xs text-[#64748b] uppercase tracking-wider">Payees</p>
            <p className="text-xl font-bold text-[#10B981] mt-1">{summary.paid_commissions.toLocaleString("fr-FR")} EUR</p>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid="comm-affiliates">
            <p className="text-xs text-[#64748b] uppercase tracking-wider">Affilies actifs</p>
            <p className="text-xl font-bold text-[#0099CC] mt-1">{summary.active_affiliates}</p>
            <p className="text-xs text-[#64748b]">{summary.clients_with_affiliate} clients affilies</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {[
          { key: "all", label: "Toutes" },
          { key: "pending", label: "En attente" },
          { key: "paid", label: "Payees" },
        ].map(f => (
          <Button key={f.key} variant={filter === f.key ? "default" : "outline"} size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
            data-testid={`filter-${f.key}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* Commissions list */}
      {commissions.length === 0 ? (
        <div className="text-center py-12 bg-[#151B2B] border border-[#2A3441] rounded-xl">
          <HandCoins className="h-10 w-10 text-[#64748b] mx-auto mb-3" />
          <p className="text-[#94A3B8]">Aucune commission enregistree.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {commissions.map(c => {
            const cfg = statusCfg[c.status] || statusCfg.pending;
            const Icon = cfg.icon;
            return (
              <div key={c.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4 flex items-center gap-4"
                data-testid={`commission-${c.id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg}`}>
                  <Icon className={`h-5 w-5 ${cfg.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-medium">{c.commission_amount.toLocaleString("fr-FR")} EUR</p>
                    <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                    <span className="text-xs text-[#64748b]">({c.commission_rate}%)</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5">
                    {c.affiliate_name} &rarr; {c.client_name} &mdash; Paiement: {c.payment_amount.toLocaleString("fr-FR")} EUR
                  </p>
                  <p className="text-xs text-[#64748b]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                {c.status === "pending" && (
                  <Button variant="outline" size="sm" onClick={() => markPaid(c.id)}
                    className="border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10 shrink-0"
                    data-testid={`pay-commission-${c.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Payer
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AffiliatesPage() {
  const [activeTab, setActiveTab] = useState("affiliates");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="affiliates-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Affilies & Commissions</h1>
        <p className="text-[#94A3B8] mt-1">Gerez vos affilies et suivez les commissions generees.</p>
      </div>

      <div className="flex gap-1 border-b border-[#2A3441]" data-testid="affiliates-tabs">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key ? "border-[#0099CC] text-[#0099CC]" : "border-transparent text-[#94A3B8] hover:text-white"
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="h-4 w-4" />{tab.label}
          </button>
        ))}
      </div>

      {activeTab === "affiliates" && <AffiliatesTab />}
      {activeTab === "commissions" && <CommissionsTab />}
    </div>
  );
}
